/* eslint-disable camelcase */
import { useMemo } from 'react'
import { VisuallyHidden, Link } from '@overdose/components'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { ResolvedCustomerType } from 'shared-types'
import { ProductImage } from '~/components'

import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import {
  filterProductTags,
  getProductPriceBadge,
  ProductPriceDiscountType,
  resolveProductPriceDisplay,
} from '~/helpers'
import {
  useAlgoliaSearchInsignts,
  useCompareProducts,
  usePage,
  useUserPreferredStore,
} from '~/hooks'
import { useProductDataLayer } from '~/lib/gtm/hooks'
import { Stack } from '../Stack'
import AddToCartButton from './AddToCartButton'
import AddToListButton from './AddToListButton'
import HelperText from './HelperText'
import styles from './ProductCard.module.css'
import { ProductCardProps } from './ProductCard.types'
import ProductCardTag from './ProductCardTag'
import { ProductPrice } from './ProductPrice/ProductPrice'

const PRICE_TAG_CLASS_NAMES: Record<ProductPriceDiscountType, string> = {
  [ProductPriceDiscountType.Promo]: styles.priceTagPromo,
  [ProductPriceDiscountType.Member]: styles.priceTagMember,
  [ProductPriceDiscountType.Trade]: styles.priceTagTrade,
  [ProductPriceDiscountType.Staff]: styles.priceTagStaff,
}

const Checkbox = dynamic(
  async () => {
    const mod = await import('../Checkbox')
    return mod.Checkbox
  },
  {
    ssr: false,
  }
)

export const ProductCard = ({
  thumbnail,
  finalPrice,
  regularPrice,
  memberPrice,
  hasPromoDiscount,
  hasCustomerTypeDiscount,
  resolvedCustomerType,
  title,
  url,
  sku,
  showAddToCartButton = false,
  className,
  brandName,
  isFeatured = false,
  inStockText,
  availableForDelivery,
  showWishlistButton = true,
  reviewCount,
  rating,
  tag = '',
  index,
  category,
  category1Name,
  category2Name,
  category3Name,
  category4Name,
  category5Name,
  listId,
  listName,
  item_status_tags,
  imagePriority = false,
  inStock,
  promotionId,
  promotionName,
  creativeName,
  creativeSlot,
  titleSection,
  isShowCheck = true,
  noImage = false,
  tags = [],
  searchIndex = 0,
  inStore,
  objectId,
  queryId,
  externalUrl,
  isAvailableExternalLink = false,
  titleClass,
  titleLinkClass,
  isUpsell = false,
  showRating = true,
  showCompactAddToCart = true,
  showStockAvailability = true,
  isEndlessAisleFromSupplier = false,
  isEndlessAisleOnHand = false,
  inStoreEndlessAisle = false,
}: ProductCardProps) => {
  const { isProductInCompare, toggleProduct, canAddRemoveToCompare } =
    useCompareProducts()
  const {
    dispatchSelectItemDataLayerEvent,
    dispatchSelectPromotionDataLayerEvent,
  } = useProductDataLayer()
  const { sendClickEvent } = useAlgoliaSearchInsignts()
  const { siteCode } = usePage()
  const { store } = useUserPreferredStore()
  const productUrl =
    !!externalUrl && isAvailableExternalLink ? externalUrl : url

  const filteredTags = useMemo(() => {
    return filterProductTags(tags, isEndlessAisleFromSupplier)
  }, [tags, isEndlessAisleFromSupplier])

  // The first tag with a description is shown beside the price in place of a discount badge
  const priceTag = useMemo(() => {
    return filteredTags.find((filteredTag) => {
      return !!filteredTag.desc
    })
  }, [filteredTags])

  // Without a described tag, the price badge is mirrored onto the image as a tag. Trade customers
  // only see lifecycle tags on the image.
  const priceBadge = useMemo(() => {
    if (priceTag || resolvedCustomerType === ResolvedCustomerType.Trade) {
      return null
    }

    const badge = getProductPriceBadge(
      resolveProductPriceDisplay({
        finalPrice,
        regularPrice,
        memberPrice,
        hasPromoDiscount,
        hasCustomerTypeDiscount,
        resolvedCustomerType,
      })
    )

    return badge.label ? badge : null
  }, [
    priceTag,
    finalPrice,
    regularPrice,
    memberPrice,
    hasPromoDiscount,
    hasCustomerTypeDiscount,
    resolvedCustomerType,
  ])

  const product = useMemo(() => {
    return {
      thumbnail,
      finalPrice,
      regularPrice,
      title,
      url,
      sku,
      showAddToCartButton,
      className,
      brandName,
      isFeatured,
      inStockText,
      availableForDelivery,
      showWishlistButton,
      reviewCount,
      rating,
      tag,
      category1Name,
      category2Name,
      category3Name,
      category4Name,
      category5Name,
      listId,
      listName,
      item_status_tags,
      inStock,
      filteredTags,
    }
  }, [
    thumbnail,
    finalPrice,
    regularPrice,
    title,
    url,
    sku,
    showAddToCartButton,
    className,
    brandName,
    isFeatured,
    inStockText,
    availableForDelivery,
    showWishlistButton,
    reviewCount,
    rating,
    tag,
    category1Name,
    category2Name,
    category3Name,
    category4Name,
    category5Name,
    listId,
    listName,
    item_status_tags,
    inStock,
    filteredTags,
  ])
  const isPromotion =
    !!promotionId && !!promotionName && !!creativeName && !!creativeSlot
  const isStockAvailabilityVisible =
    (availableForDelivery || inStock || inStockText) && showStockAvailability

  const handleClick = () => {
    sendClickEvent(
      [{ title, url, listId, objectId }],
      !!searchIndex && [searchIndex],
      queryId
    )

    return isFeatured && isPromotion
      ? dispatchSelectPromotionDataLayerEvent({
          product,
          index,
          category,
          promotionId,
          promotionName,
          creativeName,
          creativeSlot,
        })
      : dispatchSelectItemDataLayerEvent(product, index, category)
  }

  return (
    <div
      className={classNames('flex flex-col justify-between h-full', className)}>
      {!noImage && (
        <div className='relative rounded-md p-4 bg-background-thumb product_image'>
          <div className='relative rounded-md overflow-hidden aspect-square'>
            {thumbnail?.altText && thumbnail?.src && (
              <ProductImage
                src={thumbnail?.src}
                alt={thumbnail?.altText}
                width={thumbnail?.width}
                height={thumbnail?.height}
                className='object-contain w-full h-full'
                sizes='(min-width: 1536px) 17vw, (min-width: 1366px) 230px, (min-width: 1024px) 260px, (min-width: 640px) 285px, 260px'
                priority={imagePriority}
              />
            )}
          </div>
          <div className='absolute left-2.5 top-2.5 pr-11'>
            <Stack direction='horizontal' spacing='xs'>
              {priceBadge && (
                <ProductCardTag
                  tag={{ desc: priceBadge.label }}
                  className={
                    priceBadge.type && PRICE_TAG_CLASS_NAMES[priceBadge.type]
                  }
                />
              )}
              {filteredTags?.map((tag, index) => {
                return <ProductCardTag key={index} tag={tag} />
              })}
            </Stack>
          </div>

          {productUrl ? (
            <Link
              to={productUrl}
              title={`${title} details`}
              className='absolute inset-0'
              data-id='viewItemButton'
              onClick={handleClick}>
              <VisuallyHidden>{title} details</VisuallyHidden>
            </Link>
          ) : (
            <VisuallyHidden>{title} details</VisuallyHidden>
          )}
          {!isFeatured && isShowCheck && (
            <div className='absolute right-2.5 top-2.5'>
              <Checkbox
                label=''
                name={sku}
                size='lg'
                defaultChecked={false}
                disabled={!canAddRemoveToCompare(sku)}
                checked={isProductInCompare(sku)}
                onChange={() => {
                  toggleProduct(product)
                }}
                theme={{
                  box: 'm-0',
                }}
              />
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          'px-1.5 lg:px-4 !pb-4 h-full flex flex-col justify-between',
          isFeatured ? 'pt-4' : 'pt-2'
        )}>
        <div className='flex flex-col grow'>
          {brandName && (
            <Typography
              variant={TypographyVariant.BodyRegular}
              tag={TypographyTag.p}
              className={classNames(styles.brand, 'mb-1 md:h-4')}>
              {brandName}
            </Typography>
          )}
          {productUrl && (
            <Typography
              tag={TypographyTag.h3}
              variant={TypographyVariant.BodyLargeBold}
              className={classNames(
                'hover:underline lg:max-h-[72px]',
                styles.cardTitle,
                {
                  [styles.featuredCardTitle]: isFeatured,
                  'lg:max-h-[72px]': !isUpsell,
                },
                titleClass
              )}>
              <Link
                to={productUrl}
                variant='secondary'
                data-id='viewItemLink'
                title={`${title} details`}
                onClick={handleClick}
                className={classNames(
                  'md:line-clamp-3 overflow-wrap-anywhere',
                  titleLinkClass
                )}>
                <span dangerouslySetInnerHTML={{ __html: title }} />
              </Link>
            </Typography>
          )}

          {sku && (
            <Typography
              variant={TypographyVariant.BodyRegular}
              tag={TypographyTag.p}
              className='text-greyscale-600 mt-4'>
              <span className='whitespace-nowrap font-bold'>{sku}</span>
            </Typography>
          )}

          {showRating && !!rating && (
            <div className='mt-2 h-4'>
              <div
                className='rating_summary'
                data-bv-show='inline_rating'
                data-bv-product-id={sku}
              />
            </div>
          )}
        </div>

        <div>
          {(finalPrice || regularPrice) && (
            <ProductPrice
              finalPrice={finalPrice}
              regularPrice={regularPrice}
              memberPrice={memberPrice}
              hasPromoDiscount={hasPromoDiscount}
              hasCustomerTypeDiscount={hasCustomerTypeDiscount}
              resolvedCustomerType={resolvedCustomerType}
              tag={priceTag}
            />
          )}

          {sku && showAddToCartButton && (
            <div className='flex flex-wrap md:flex-nowrap items-center my-2 gap-x-2 gap-y-1 productCard-addToCart'>
              <AddToCartButton
                sku={sku}
                inStock={inStock}
                searchIndex={searchIndex}
                titleSection={titleSection}
                queryId={queryId}
                isButtonOnly={showCompactAddToCart}
                externalUrl={externalUrl}
                tooltipPosition='top'
              />
              <AddToListButton
                product={product}
                queryId={queryId}
                buttonProps={{
                  variant: 'ghost',
                  size: 'sm',
                  iconOnly: true,
                  className: '!w-[24px] !h-[40px] !min-w-[24px] !min-h-[40px]',
                }}
                tooltipPosition='top'
              />
            </div>
          )}
          {isStockAvailabilityVisible && (
            <HelperText
              siteCode={siteCode}
              availableForDelivery={availableForDelivery}
              inStockText={inStockText}
              inStore={inStore}
              inStoreEndlessAisle={inStoreEndlessAisle}
              inStock={inStock}
              isEndlessAisleFromSupplier={isEndlessAisleFromSupplier}
              isEndlessAisleOnHand={isEndlessAisleOnHand}
              hasSelectedStore={store != null}
            />
          )}
        </div>
      </div>
    </div>
  )
}
